import { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import { isOrgInFreeTier } from '../../../helpers/subscriptions';
import type { ReportFlow, ReportsV3QueryParams } from '../types';

import { CLEARED_CUSTOM_PARAMS, STANDARD_DEFAULTS } from './useCustomReportOptions';

const flowsSet = new Set<ReportFlow>(['standard', 'custom']);

/**
 * This hook helps to manage the report "flow" (standard vs custom).
 * This is stored in the URL query params, and is used to determine which report options are shown.
 * If there is no flow set (or if its invalid), the effect here will set it.
 *
 * When switching to custom, the URL is seeded with the standard defaults so
 * the user starts with the same options and can adjust from there.
 * When switching back to standard, all custom-specific params are cleared.
 */
export const useReportFlow = (): readonly [ReportFlow, (flow: ReportFlow) => void] => {
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  const isFreeTier = isOrgInFreeTier();

  // Revert to standard flow if the flow is invalid, doesn't exist, or is custom on free tier.
  useEffect(() => {
    if (!query.flow || !flowsSet.has(query.flow) || (query.flow === 'custom' && isFreeTier)) {
      setQuery({ ...query, ...CLEARED_CUSTOM_PARAMS, flow: 'standard' });
    }
  }, [query, setQuery, isFreeTier]);

  const flow: ReportFlow = useMemo(() => {
    if (!query.flow || !flowsSet.has(query.flow) || (query.flow === 'custom' && isFreeTier)) {
      return 'standard';
    }

    return query.flow;
  }, [query, isFreeTier]);

  const setFlow = useCallback((newFlow: ReportFlow) => {
    if (newFlow === 'custom') {
      setQuery({ ...query, ...STANDARD_DEFAULTS, flow: 'custom' });
    } else {
      setQuery({ ...query, ...CLEARED_CUSTOM_PARAMS, flow: 'standard' });
    }
  }, [query, setQuery]);

  return [
    flow,
    setFlow,
  ];
};
